import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { useTranslation } from '../i18n/LanguageContext';
import {
  MAX_BUDGET,
  getRemainingForCategory,
  isBudgetAllocationValid,
  totalCategoryBudget,
  rebalanceCategoryBudgets,
  categoryInteractionMax,
} from '../utils/budgetConstraints';

const PREDEFINED_CATEGORY_KEYS = new Set(['courses', 'vetements', 'restaurant', 'loisirs']);

const getCategoryLabel = (t, key) =>
  PREDEFINED_CATEGORY_KEYS.has(key)
    ? t(`budget.categories.${key}`)
    : key.charAt(0).toUpperCase() + key.slice(1);

function BudgetDashboard() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [savingTarget, setSavingTarget] = useState(null);
  const [summary, setSummary] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', budget: '' });
  const [savingNewCategory, setSavingNewCategory] = useState(false);
  const [form, setForm] = useState({
    global_budget: '',
    category_budgets: { courses: '', vetements: '', restaurant: '', loisirs: '' }
  });

  const loadSummary = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/budget/monthly-summary');
      const nextSummary = response.data;
      setSummary(nextSummary);
      setForm({
        global_budget: String(nextSummary.global_budget ?? ''),
        category_budgets: nextSummary.categories.reduce((acc, cat) => {
          acc[cat.category] = String(cat.budget ?? '');
          return acc;
        }, { courses: '', vetements: '', restaurant: '', loisirs: '' })
      });
    } catch (error) {
      toast.error(t('budget.errors.load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSummary(); }, []); // eslint-disable-line

  // ── Derived state ────────────────────────────────────────────────────────────

  const currentValues = useMemo(() => ({
    global: Math.max(0, parseFloat(form.global_budget || 0)),
    categories: Object.fromEntries(
      Object.entries(form.category_budgets).map(([k, v]) => [k, Math.max(0, parseFloat(v || 0))])
    )
  }), [form]);

  /**
   * displayBudgets — always satisfies sum ≤ global.
   * Used for slider values, Input values, and card header numbers.
   * If form state is temporarily invalid, values are proportionally scaled down.
   */
  const displayBudgets = useMemo(() => {
    const { global, categories } = currentValues;
    if (isBudgetAllocationValid(global, categories)) return categories;
    const rebalanced = rebalanceCategoryBudgets(
      Object.fromEntries(Object.entries(categories).map(([k, v]) => [k, String(v)])),
      global
    );
    return Object.fromEntries(Object.entries(rebalanced).map(([k, v]) => [k, Math.max(0, Number(v) || 0)]));
  }, [currentValues]);

  const totalCategoryAllocation = useMemo(
    () => totalCategoryBudget(currentValues.categories),
    [currentValues.categories]
  );
  const categoryOverAllocation = Math.max(0, totalCategoryAllocation - currentValues.global);
  const isCategoryAllocationValid = isBudgetAllocationValid(currentValues.global, currentValues.categories);

  // ── Invariant guard ──────────────────────────────────────────────────────────
  // If form.category_budgets somehow sums above form.global_budget, rebalance.
  // Functional updater reads the LATEST committed state, never a stale snapshot.
  useLayoutEffect(() => {
    setForm((previous) => {
      const prevGlobal = Math.max(0, Number(previous.global_budget) || 0);
      const prevTotal = totalCategoryBudget(previous.category_budgets);
      if (prevTotal <= prevGlobal + 0.009) return previous;
      return { ...previous, category_budgets: rebalanceCategoryBudgets(previous.category_budgets, prevGlobal) };
    });
  }, [form.global_budget, form.category_budgets]);

  // ── State mutators ───────────────────────────────────────────────────────────

  const updateCategoryBudget = useCallback((category, value) => {
    setForm((previous) => {
      const numeric = Math.max(0, Number(value) || 0);
      const maxAllowed = categoryInteractionMax(category, previous.global_budget, previous.category_budgets);
      const clamped = Math.min(maxAllowed, numeric);
      return { ...previous, category_budgets: { ...previous.category_budgets, [category]: String(clamped) } };
    });
  }, []);

  const setBudgetValue = useCallback((key, value) => {
    const numericValue = Math.min(MAX_BUDGET, Math.max(0, Number(value) || 0));
    if (key === 'global') {
      setForm((previous) => {
        const prevTotal = totalCategoryBudget(previous.category_budgets);
        if (prevTotal <= numericValue + 0.009) return { ...previous, global_budget: String(numericValue) };
        return {
          ...previous,
          global_budget: String(numericValue),
          category_budgets: rebalanceCategoryBudgets(previous.category_budgets, numericValue),
        };
      });
      return;
    }
    updateCategoryBudget(key, String(numericValue));
  }, [updateCategoryBudget]);

  const clampBudgetValueNow = useCallback((key, value) => {
    setBudgetValue(key, value);
  }, [setBudgetValue]);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const buildCategoryPayload = (budgetsOverride) => {
    const budgets = budgetsOverride ?? form.category_budgets;
    return Object.fromEntries(Object.entries(budgets).map(([k, v]) => [k, parseFloat(v || 0)]));
  };

  const handleSave = async (targetKey) => {
    if (!isCategoryAllocationValid) {
      toast.error(t('budget.errors.categorySumExceedsGlobal', { extra: categoryOverAllocation.toFixed(2) }));
      return;
    }
    try {
      setSavingTarget(targetKey);
      await axios.put('/budget/monthly-config', {
        global_budget: parseFloat(form.global_budget || 0),
        category_budgets: buildCategoryPayload()
      });
      toast.success(t('budget.success.saved'));
      await loadSummary();
      setEditingKey(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('budget.errors.save'));
    } finally {
      setSavingTarget(null);
    }
  };

  const handleAddCustomCategory = async () => {
    const name = newCategory.name.trim().toLowerCase();
    if (!name) return;
    try {
      setSavingNewCategory(true);
      const requested = Math.min(MAX_BUDGET, Math.max(0, Number(newCategory.budget) || 0));
      const allowed = getRemainingForCategory(name, form.global_budget, form.category_budgets);
      const clamped = Math.min(requested, allowed);
      const updatedBudgets = { ...form.category_budgets, [name]: String(clamped) };
      if (requested > clamped + 0.009) toast.error(t('budget.errors.categoryClampedToGlobal', { amount: clamped.toFixed(2) }));
      await axios.put('/budget/monthly-config', {
        global_budget: parseFloat(form.global_budget || 0),
        category_budgets: buildCategoryPayload(updatedBudgets)
      });
      toast.success(`Catégorie "${name}" ajoutée`);
      setNewCategory({ name: '', budget: '' });
      setShowAddCategory(false);
      await loadSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('budget.errors.save'));
    } finally {
      setSavingNewCategory(false);
    }
  };

  const handleDeleteCustomCategory = async (catKey) => {
    try {
      const updatedBudgets = Object.fromEntries(Object.entries(form.category_budgets).filter(([k]) => k !== catKey));
      await axios.put('/budget/monthly-config', {
        global_budget: parseFloat(form.global_budget || 0),
        category_budgets: buildCategoryPayload(updatedBudgets)
      });
      toast.success(`Catégorie supprimée`);
      await loadSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('budget.errors.save'));
    }
  };

  const getGlobalSliderMax = (spent, budget, suggestedBudget) => {
    const reference = Math.max(spent || 0, budget || 0, suggestedBudget || 0, 50);
    return Math.min(MAX_BUDGET, Math.ceil((reference * 1.6 + 50) / 10) * 10);
  };

  const isDirty = (key, currentBudget) => {
    const localValue = key === 'global'
      ? parseFloat(form.global_budget || 0)
      : parseFloat(form.category_budgets[key] || 0);
    return Math.abs(localValue - currentBudget) > 0.009;
  };

  const renderProgress = (spent, budget) => {
    const ratio = budget > 0 ? Math.min(100, Math.round((spent / budget) * 100)) : 0;
    const barColor = ratio >= 100 ? 'bg-red-500' : ratio >= 95 ? 'bg-orange-500' : ratio >= 80 ? 'bg-amber-500' : 'bg-emerald-500';
    return (
      <div className="space-y-2">
        <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
          <div className={`h-full ${barColor}`} style={{ width: `${ratio}%` }} />
        </div>
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>{t('budget.labels.spent', { amount: spent.toFixed(2) })}</span>
          <span>{ratio}%</span>
        </div>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading || !summary) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-gray-600">{t('budget.loading')}</CardContent>
      </Card>
    );
  }

  const globalSliderMax = getGlobalSliderMax(summary.global_spent, currentValues.global, summary.suggested_global_budget);
  const isEditingGlobal = editingKey === 'global';

  return (
    <div className="grid gap-4 sm:gap-6" data-testid="budget-dashboard-root">
      <Card className="border-emerald-200 bg-emerald-50">
        <CardHeader>
          <CardTitle>{t('budget.title')}</CardTitle>
          <CardDescription>{t('budget.description')}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm text-gray-600">{t('budget.period')}</p>
                <p className="text-lg font-semibold text-gray-900">{summary.month_label}</p>
              </div>
              <Badge variant="secondary">
                {summary.source === 'configured' ? t('budget.badges.customized') : t('budget.badges.suggested')}
              </Badge>
            </div>

            {/* ── Global budget card ──────────────────────────────────── */}
            <div className="rounded-xl border bg-white p-4 space-y-3 transition-shadow hover:shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('budget.global')}</p>
                  <p className="text-2xl font-bold text-gray-900">{currentValues.global.toFixed(2)} €</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <div className="text-right">
                    <p className="text-sm text-gray-600">{t('budget.remaining')}</p>
                    <p className="text-xl font-semibold" style={{ color: (currentValues.global - summary.global_spent) < 0 ? '#dc2626' : '#059669' }}>
                      {Math.max(0, currentValues.global - summary.global_spent).toFixed(2)} €
                    </p>
                  </div>
                  {!isEditingGlobal ? (
                    <Button type="button" size="sm" variant="outline"
                      aria-label="edit-budget-global"
                      className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 mt-1"
                      onClick={() => setEditingKey('global')}>
                      ✏️ Modifier
                    </Button>
                  ) : (
                    <Button type="button" size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white mt-1"
                      disabled={savingTarget === 'global'}
                      onClick={() => handleSave('global')}>
                      {savingTarget === 'global' ? '...' : '✅ OK'}
                    </Button>
                  )}
                </div>
              </div>
              {renderProgress(summary.global_spent, currentValues.global)}

              <div className={`rounded-lg border p-3 text-sm ${isCategoryAllocationValid ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'}`}>
                <p className="font-semibold">{t('budget.masterRuleTitle')}</p>
                <p className="mt-1">{t('budget.masterRuleHint')}</p>
                <p className="mt-2">{t('budget.allocationSummary', { total: totalCategoryAllocation.toFixed(2), global: currentValues.global.toFixed(2) })}</p>
                {!isCategoryAllocationValid && (
                  <p className="mt-1 font-medium">{t('budget.errors.categorySumExceedsGlobal', { extra: categoryOverAllocation.toFixed(2) })}</p>
                )}
              </div>

              <p className="text-xs text-gray-500">{t('budget.suggestedGlobal', { amount: summary.suggested_global_budget.toFixed(2) })}</p>

              <div className="space-y-3 rounded-lg border border-emerald-100 bg-emerald-50/40 p-3">
                <div className="flex items-center justify-between gap-3 text-xs text-gray-600">
                  <span>0 €</span>
                  <span className="font-semibold text-emerald-700">{currentValues.global.toFixed(0)} €</span>
                  <span>{MAX_BUDGET.toLocaleString('fr-FR')} €</span>
                </div>
                <input type="range" min={0} max={globalSliderMax} step="5"
                  aria-label="budget-slider-global"
                  value={currentValues.global}
                  disabled={!isEditingGlobal}
                  onChange={(e) => setBudgetValue('global', e.target.value)}
                  onMouseUp={(e) => setBudgetValue('global', e.target.value)}
                  onTouchEnd={(e) => clampBudgetValueNow('global', e.target.value)}
                  onBlur={(e) => clampBudgetValueNow('global', e.target.value)}
                  className={`w-full accent-emerald-600 ${!isEditingGlobal ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                />
                {isEditingGlobal && (
                  <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto] sm:items-end">
                    <Input id="global-budget-inline" type="number" min={0} max={MAX_BUDGET} step="0.01"
                      aria-label="budget-input-global"
                      value={form.global_budget}
                      onChange={(e) => setBudgetValue('global', e.target.value)}
                      onBlur={(e) => clampBudgetValueNow('global', e.target.value)}
                      placeholder="Montant en €"
                    />
                    <p className="text-xs text-gray-500 sm:col-span-3">
                      {t('budget.globalMinByCategories', { amount: totalCategoryAllocation.toFixed(2) })}
                    </p>
                    <Button type="button" variant="outline" onClick={() => setBudgetValue('global', summary.suggested_global_budget)}>
                      {t('budget.useSuggestion')}
                    </Button>
                    <Button type="button" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      disabled={savingTarget === 'global' || !isDirty('global', summary.global_budget) || !isCategoryAllocationValid}
                      onClick={() => handleSave('global')}>
                      {savingTarget === 'global' ? t('budget.saving') : '✅ OK'}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* ── Category cards ──────────────────────────────────────── */}
            <div className="grid gap-3 md:grid-cols-2">
              {summary.categories.map((category) => {
                const catKey = category.category;

                // displayBudgets is always valid (sum ≤ global)
                const displayValue = displayBudgets[catKey] ?? 0;

                // Strict UX rule shared with write-time clamping.
                // If global budget is fully allocated, sliderMax equals current value,
                // so increasing to the right is physically blocked.
                const sliderMax = categoryInteractionMax(catKey, currentValues.global, displayBudgets);

                // Defensive: slider value never exceeds its own max
                const sliderValue = Math.min(displayValue, sliderMax);

                const isEditingCat = editingKey === catKey;
                const remaining = displayValue - category.spent;

                return (
                  <div key={catKey} className="rounded-xl border bg-white p-4 space-y-3 transition-shadow hover:shadow-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-gray-900">{getCategoryLabel(t, catKey)}</p>
                        <p className="text-xs text-gray-500">{t('budget.suggestedCategory', { amount: category.suggested_budget.toFixed(2) })}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Badge variant="outline" style={{ backgroundColor: remaining < 0 ? '#fee2e2' : '#f0fdf4', color: remaining < 0 ? '#dc2626' : '#059669' }}>
                          {Math.max(0, remaining).toFixed(2)} €
                        </Badge>
                        {category.is_custom && (
                          <button type="button" title="Supprimer cette catégorie"
                            className="text-gray-400 hover:text-red-500 text-xs leading-none ml-1"
                            onClick={() => handleDeleteCustomCategory(catKey)}>✕</button>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">{t('budget.cardBudget')}</p>
                        <p className="text-xl font-bold text-gray-900">{displayValue.toFixed(2)} €</p>
                      </div>
                      {!isEditingCat ? (
                        <Button type="button" size="sm" variant="outline"
                          aria-label={`edit-budget-${catKey}`}
                          className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => setEditingKey(catKey)}>
                          ✏️ Modifier
                        </Button>
                      ) : (
                        <Button type="button" size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          disabled={savingTarget === catKey}
                          onClick={() => handleSave(catKey)}>
                          {savingTarget === catKey ? '...' : '✅ OK'}
                        </Button>
                      )}
                    </div>

                    {renderProgress(category.spent, displayValue)}

                    <div className="space-y-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
                      <div className="flex items-center justify-between gap-3 text-xs text-gray-600">
                        <span>0 €</span>
                        <span className="font-semibold">{sliderValue.toFixed(0)} €</span>
                        {/* Dynamic cap label — shows the REAL allowed max, not MAX_BUDGET */}
                        <span className="font-medium text-emerald-700">{sliderMax.toFixed(0)} € max</span>
                      </div>
                      <input type="range" min="0" max={sliderMax} step="0.01"
                        aria-label={`budget-slider-${catKey}`}
                        value={sliderValue}
                        disabled={!isEditingCat}
                        onChange={(e) => setBudgetValue(catKey, e.target.value)}
                        onMouseUp={(e) => setBudgetValue(catKey, e.target.value)}
                        onTouchEnd={(e) => clampBudgetValueNow(catKey, e.target.value)}
                        onBlur={(e) => clampBudgetValueNow(catKey, e.target.value)}
                        className={`w-full accent-emerald-600 ${!isEditingCat ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      />
                      {isEditingCat && (
                        <div className="space-y-2">
                          <Input id={`budget-inline-${catKey}`} type="number" min="0"
                            aria-label={`budget-input-${catKey}`}
                            max={sliderMax}
                            step="0.01"
                            value={sliderValue}
                            onChange={(e) => setBudgetValue(catKey, e.target.value)}
                            onBlur={(e) => clampBudgetValueNow(catKey, e.target.value)}
                            placeholder="Montant en €"
                          />
                          <p className="text-xs text-gray-500">
                            {t('budget.maxAllowedForCategory', { amount: sliderMax.toFixed(2) })}
                          </p>
                          <div className="flex gap-2">
                            <Button type="button" variant="outline" size="sm"
                              onClick={() => setBudgetValue(catKey, category.suggested_budget)}>
                              {t('budget.useSuggestion')}
                            </Button>
                            <Button type="button" size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              disabled={savingTarget === catKey || !isDirty(catKey, category.budget) || !isCategoryAllocationValid}
                              onClick={() => handleSave(catKey)}>
                              {savingTarget === catKey ? t('budget.saving') : '✅ OK'}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Add custom category ─────────────────────────────────── */}
            {!showAddCategory ? (
              <button type="button"
                className="flex items-center gap-2 text-sm text-emerald-700 hover:text-emerald-900 font-medium mt-1"
                onClick={() => setShowAddCategory(true)}>
                ➕ Ajouter une catégorie personnalisée
              </button>
            ) : (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-800">Nouvelle catégorie de budget</p>
                <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto_auto]">
                  <Input type="text" placeholder="Nom (ex: sport, pharmacie…)"
                    value={newCategory.name} maxLength={40}
                    onChange={(e) => setNewCategory((p) => ({ ...p, name: e.target.value }))} />
                  <Input type="number" min="0" max={MAX_BUDGET} step="5" placeholder="Budget en €"
                    value={newCategory.budget}
                    onChange={(e) => setNewCategory((p) => ({ ...p, budget: e.target.value }))} />
                  <Button type="button" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={savingNewCategory || !newCategory.name.trim()}
                    onClick={handleAddCustomCategory}>
                    {savingNewCategory ? '...' : '✅ Ajouter'}
                  </Button>
                  <Button type="button" variant="outline"
                    onClick={() => { setShowAddCategory(false); setNewCategory({ name: '', budget: '' }); }}>
                    Annuler
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* ── Smart alerts ──────────────────────────────────────────── */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('budget.smartAlertsTitle')}</CardTitle>
                <CardDescription>{t('budget.smartAlertsDescription')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {summary.alerts.length === 0 && (
                  <p className="text-sm text-gray-600">{t('budget.noAlerts')}</p>
                )}
                {summary.alerts.map((alert) => (
                  <div key={`${alert.label}-${alert.level}`} className="rounded-lg border bg-gray-50 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm text-gray-800">{t(`budget.alertMessages.${alert.level}`, { label: getCategoryLabel(t, alert.label) })}</p>
                      <Badge variant={alert.level === 'exceeded' ? 'destructive' : 'secondary'}>
                        {t(`budget.alertLevels.${alert.level}`)}
                      </Badge>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default BudgetDashboard;
